import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { HiOutlineDocumentText, HiOutlineClock, HiOutlineCash, HiOutlineUserCircle, HiPlus, HiOutlineFilter } from "react-icons/hi";
import { BsStarFill } from "react-icons/bs";
import { IoCalendarOutline } from "react-icons/io5";

import api from "@/services/api";
import Modal from "@/components/modal";
import Pagination from "@/components/Paginator";
import { classNames } from "@/utils";

const ListAnswers = ({ status }) => {
  const [total, setTotal] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [filter, setFilter] = useState({ page: 1, pageSize: 10 });
  const [openModal, setOpenModal] = useState(false);
  const [newAnswer, setNewAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadAnswers();
  }, [filter, status]);

  async function loadAnswers() {
    try {
      setIsLoading(true);
      const { ok, data, total } = await api.post("/mission_ao_answer/search", { ...filter, status });
      if (!ok) {
        console.error("Error fetching answers");
        toast.error("Impossible de charger les appels d'offres");
        return;
      }
      setAnswers(data);
      setTotal(total);
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  }

  async function addAnswer() {
    if (!newAnswer.trim()) {
      toast.error("Le nom de l'AO ne peut pas être vide");
      return;
    }

    try {
      const { ok, data } = await api.post("/mission_ao_answer", { title: newAnswer });
      if (!ok) throw new Error("Failed to add answer");
      toast.success("AO créé avec succès!");
      setNewAnswer("");
      setOpenModal(false);
      navigate(`/rfp/answers/${data._id}`);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la création de l'AO");
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      approved: { bg: "bg-green-100", text: "text-green-800", label: "Approuvé" },
      pending: { bg: "bg-yellow-100", text: "text-yellow-800", label: "En attente" },
      need_info: { bg: "bg-purple-100", text: "text-purple-800", label: "Besoin d'info" },
      "waiting result": { bg: "bg-blue-100", text: "text-blue-800", label: "En attente du résultat" },
      rejected: { bg: "bg-red-100", text: "text-red-800", label: "Rejeté" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>;
  };

  const calculateDaysLeft = (date) => {
    if (!date) return null;

    const today = new Date();
    const dueDate = new Date(date);
    const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) return null;

    let badgeStyle = "bg-green-100 text-green-800";
    if (daysLeft < 3) badgeStyle = "bg-red-100 text-red-800";
    else if (daysLeft < 7) badgeStyle = "bg-yellow-100 text-yellow-800";

    return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badgeStyle}`}>{daysLeft}j restants</span>;
  };

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Appels d'Offres</h1>
          <p className="text-gray-600 mt-1">Gérez et suivez vos appels d'offres en cours</p>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <select
                className="input border-gray-300 pr-8 h-10 text-gray-600"
                value={filter.pageSize}
                onChange={(e) => setFilter({ ...filter, pageSize: Number(e.target.value), page: 1 })}>
                <option value={10}>10 par page</option>
                <option value={25}>25 par page</option>
                <option value={50}>50 par page</option>
                <option value={100}>100 par page</option>
              </select>
            </div>
            <button className="btn-primary flex items-center gap-2" onClick={() => setOpenModal(true)}>
              <HiPlus className="h-5 w-5" />
              <span>Nouvel AO</span>
            </button>
          </div>

          {isLoading ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
            </div>
          ) : answers.length === 0 ? (
            <div className="p-8 text-center">
              <div className="mx-auto h-16 w-16 bg-gray-100 flex items-center justify-center rounded-full">
                <HiOutlineDocumentText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun appel d'offres</h3>
              <p className="mt-2 text-sm text-gray-500">Commencez par créer un nouvel appel d'offres</p>
              <div className="mt-6">
                <button onClick={() => setOpenModal(true)} className="btn-primary inline-flex items-center gap-2">
                  <HiPlus className="h-5 w-5" />
                  <span>Nouvel AO</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <HiOutlineDocumentText className="mr-2 h-4 w-4" />
                        Titre
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <IoCalendarOutline className="mr-2 h-4 w-4" />
                        Date
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <HiOutlineCash className="mr-2 h-4 w-4" />
                        Montant
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <HiOutlineUserCircle className="mr-2 h-4 w-4" />
                        Responsable
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center">
                        <BsStarFill className="mr-2 h-3 w-3" />
                        Note
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center justify-center">
                        <HiOutlineFilter className="mr-2 h-4 w-4" />
                        Statut
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {answers.map((answer) => (
                    <tr key={answer._id} className="hover:bg-gray-50 cursor-pointer transition-colors duration-150" onClick={() => navigate(`/rfp/answers/${answer._id}`)}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900 truncate max-w-xs">{answer.title || "Sans titre"}</div>
                        {answer.description && <div className="text-sm text-gray-500 truncate max-w-xs mt-1">{answer.description}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          {answer.date ? (
                            <>
                              <span className="text-sm text-gray-900">{new Date(answer.date).toLocaleDateString("fr-FR")}</span>
                              <div className="mt-1">{calculateDaysLeft(answer.date)}</div>
                            </>
                          ) : (
                            <span className="text-sm text-gray-500">Non définie</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {answer.montant ? (
                          <span className="text-sm font-medium text-gray-900">{new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(answer.montant)}</span>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {answer.user_avatar ? (
                            <img src={answer.user_avatar} alt={answer.user_name} className="h-8 w-8 rounded-full object-cover mr-2" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                              <HiOutlineUserCircle className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          <span className="text-sm text-gray-900">{answer.user_name || "Non assigné"}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {answer.note ? (
                            <>
                              <span className="text-sm font-medium text-gray-900 mr-1">{answer.note}</span>
                              <BsStarFill className="h-4 w-4 text-amber-400" />
                            </>
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">{getStatusBadge(answer.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && answers.length > 0 && (
            <div className="bg-white border-t border-gray-200 px-4 py-4 sm:px-6">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{Math.min(1 + (filter.page - 1) * filter.pageSize, total)}</span> à{" "}
                  <span className="font-medium">{Math.min(filter.page * filter.pageSize, total)}</span> sur <span className="font-medium">{total}</span> résultats
                </div>
                <Pagination page={filter.page} setPage={(page) => setFilter({ ...filter, page })} last={Math.ceil(total / filter.pageSize)} />
              </div>
            </div>
          )}
        </div>
      </div>

      {openModal && (
        <Modal isOpen={openModal} className="max-w-md w-full p-6" onClose={() => setOpenModal(false)}>
          <div className="mb-4">
            <h2 className="text-lg font-medium text-gray-900">Nouvel Appel d'Offres</h2>
            <p className="mt-1 text-sm text-gray-500">Créez un nouvel appel d'offres pour commencer le suivi</p>
          </div>

          <div className="mt-6">
            <label htmlFor="ao-title" className="block text-sm font-medium text-gray-700">
              Titre de l'AO
            </label>
            <textarea
              id="ao-title"
              rows="3"
              className="mt-1 input block w-full"
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              placeholder="Entrez le nom de l'appel d'offres..."
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button className="btn gray-btn" onClick={() => setOpenModal(false)}>
              Annuler
            </button>
            <button className="btn btn-primary" onClick={addAnswer} disabled={!newAnswer.trim()}>
              Créer
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ListAnswers;
